import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';

import { UserProfilePortalEndpoint } from '@api/services/user-profile-portal-endpoint';
import { CreditUnionRole as CreditUnionRoleType } from '@api/models/types';
import { CreditUnionsService } from '@core/services/credit-unions.service';
import { UserState } from '@core/state/user-state';
import { CreditUnionRole } from 'src/app/shared/enums/credit-union-role.enum';

const MAX_RADIO_OPTIONS = 3;

@Component({
  standalone: true,
  selector: 'app-change-credit-union-dialog',
  host: { class: 'flex flex-col flex-1' },
  imports: [FormsModule, ProgressSpinner, RadioButtonModule, SelectModule],
  templateUrl: './change-credit-union-dialog.component.html'
})
export class ChangeCreditUnionDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly userState = inject(UserState);
  private readonly userProfileEndpoint = inject(UserProfilePortalEndpoint);
  private readonly creditUnionsService = inject(CreditUnionsService);

  readonly creditUnions = computed(() => this.userState.creditUnions() ?? []);
  readonly useRadioList = computed(() => this.creditUnions().length <= MAX_RADIO_OPTIONS);

  selectedCreditUnionId = signal<string | null>(this.userState.creditUnionId() || null);
  isSaving = signal(false);
  isLoading = signal(!this.userState.creditUnions());

  canApply = computed(() => {
    const selectedId = this.selectedCreditUnionId();
    return !!selectedId && selectedId !== this.userState.creditUnionId() && !this.isSaving();
  });

  constructor() {
    if (!this.isLoading()) return;

    this.creditUnionsService.loadCreditUnions()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({ error: () => { } });
  }

  roleLabel(role: CreditUnionRoleType): string {
    return CreditUnionRole[role] ?? role;
  }

  apply() {
    const selectedId = this.selectedCreditUnionId();
    if (!selectedId || !this.canApply()) return;

    this.isSaving.set(true);
    this.userProfileEndpoint.setSelectedCreditUnion(selectedId).subscribe({
      next: () => this.dialogRef.close(true),
      error: () => this.isSaving.set(false)
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
